import React, { useRef } from 'react';
import { Control, Controller } from 'react-hook-form';
import { Platform, TextInput, View } from 'react-native';

import { OtherChip } from '@/components/services/wizard/field';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { FontPoppins } from '@/constants/font';
import type { ServiceFormData } from '@/lib/validators/service';
import { cn } from '@/utils/cn';
import { useThemedHex } from '@/hooks/use-theme-colors';

/** Atalhos comuns; o campo aceita qualquer valor, sem teto. */
const QUICK_VALUES = [50, 100, 150, 250, 400];

function format(digits: string): string {
  if (!digits) return '';
  return (parseInt(digits, 10) / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** O formulário guarda "R$ 1.234,56"; o campo grande mostra só o número. */
function stripSymbol(value: string | undefined): string {
  return (value ?? '').replace(/R\$\s?/g, '');
}

interface Props {
  control: Control<ServiceFormData>;
  /** Sufixo do valor: "/mês", "/semana" ou "/sessão". */
  unit: string;
}

/**
 * Preço em destaque: o número é a resposta da pergunta, então ocupa a largura
 * do card em vez de virar mais um campo de formulário.
 *
 * Deixar em branco é uma resposta válida — o serviço nasce sem preço padrão e
 * o valor é combinado em cada contrato.
 */
export function PriceAmountField({ control, unit }: Props) {
  const th = useThemedHex();
  const inputRef = useRef<TextInput>(null);

  return (
    <Controller
      control={control}
      name="defaultPrice"
      render={({ field, fieldState }) => {
        const isQuick = QUICK_VALUES.some(
          (value) => format(String(value * 100)) === field.value,
        );

        return (
          <View className="items-center py-2">
            <View className="flex-row items-baseline gap-1">
              <ThemedText className="text-lg font-semibold text-neutral-400">
                R$
              </ThemedText>
              <TextInput
                ref={inputRef}
                value={stripSymbol(field.value)}
                onChangeText={(text) => field.onChange(format(text.replace(/\D/g, '')))}
                onBlur={field.onBlur}
                keyboardType="number-pad"
                placeholder="0,00"
                placeholderTextColor={th('#D4D4D4')}
                accessibilityLabel="Preço"
                testID="service-wizard-price-input"
                style={[
                  {
                    fontFamily: FontPoppins.BOLD,
                    fontSize: 38,
                    lineHeight: 48,
                    color: th('#171717'),
                    minWidth: 120,
                    textAlign: 'center',
                    padding: 0,
                  },
                  // No navegador o foco desenha um anel azul do sistema.
                  Platform.OS === 'web' && ({ outline: 'none' } as never),
                ]}
              />
              <ThemedText className="text-[15px] font-semibold text-neutral-400">
                {unit}
              </ThemedText>
            </View>

            <View className="mt-3 flex-row flex-wrap justify-center gap-2">
              {QUICK_VALUES.map((value) => {
                const active = format(String(value * 100)) === field.value;
                return (
                  <PressableScale
                    key={value}
                    haptic
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    onPress={() => field.onChange(format(String(value * 100)))}
                    className={cn(
                      'h-9 items-center justify-center rounded-full px-3.5',
                      active ? 'bg-primary' : 'bg-neutral-100',
                    )}
                    style={{ borderCurve: 'continuous' }}
                  >
                    <ThemedText
                      className={cn(
                        'text-[13px] font-semibold',
                        active ? 'text-primary-foreground' : 'text-neutral-700',
                      )}
                    >
                      {value}
                    </ThemedText>
                  </PressableScale>
                );
              })}

              {/* Digitar não é gesto óbvio num número grande: "Outro" limpa e
                  abre o teclado no lugar de esperar o toque certo. */}
              <OtherChip
                active={!isQuick && !!field.value?.trim()}
                onPress={() => {
                  if (isQuick) field.onChange('');
                  inputRef.current?.focus();
                }}
              />
            </View>

            {/* Só o erro fala; preço em branco é resposta válida e se explica sozinha. */}
            {fieldState.error ? (
              <ThemedText className="mt-3 text-center text-xs leading-4 text-error-600">
                {fieldState.error.message}
              </ThemedText>
            ) : null}
          </View>
        );
      }}
    />
  );
}
